import type { CollegeSsoInfo, SsoProviderId } from '@welynk/types';
import { get, post } from './http';
import { API_ENDPOINTS } from '../api-endpoints';
import { getApiClient } from '../api-client-factory';
import type { ApiResponse } from '../api-types';
import { downloadBlob, filenameFromDisposition } from '../download';

// ── Account lifecycle & sign-in ─────────────────────────────────────────────

export class AccountApiService {
  static archive(password: string) {
    return post<{ archived: true }>(API_ENDPOINTS.AUTH.ARCHIVE_ACCOUNT, { password });
  }

  static async requestDeletion(password: string): Promise<{ deletionScheduledFor: string }> {
    const res = await getApiClient().delete<ApiResponse<{ deletionScheduledFor: string }>>(
      API_ENDPOINTS.AUTH.DELETE_ACCOUNT,
      { data: { password } }
    );
    return res.data.data;
  }

  static restore(email: string, password: string) {
    return post<{ restored: true }>(API_ENDPOINTS.AUTH.RESTORE_ACCOUNT, { email, password });
  }

  static async downloadExport(): Promise<void> {
    const res = await getApiClient().get(API_ENDPOINTS.AUTH.EXPORT_ACCOUNT, {
      responseType: 'blob',
    });
    const name = filenameFromDisposition(
      res.headers['content-disposition'] as string | undefined,
      'welynk-data.zip'
    );
    downloadBlob(res.data, name, 'application/zip');
  }
}

export class SsoApiService {
  static providers() {
    return get<{ providers: SsoProviderId[] }>(API_ENDPOINTS.AUTH.SSO_PROVIDERS);
  }

  static college(slug: string) {
    return get<CollegeSsoInfo>(API_ENDPOINTS.AUTH.SSO_COLLEGE(slug));
  }

  /** Full-page navigation target: the backend redirects to Google / Microsoft. */
  static startUrl(
    provider: SsoProviderId,
    options: { org?: string; returnTo?: string } = {}
  ): string {
    const base = (import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');
    const qs = new URLSearchParams();
    if (options.org) qs.set('org', options.org);
    if (options.returnTo) qs.set('returnTo', options.returnTo);
    const query = qs.toString();
    return `${base}${API_ENDPOINTS.AUTH.SSO_START(provider)}${query ? `?${query}` : ''}`;
  }
}
